// Streaming STT via Deepgram Nova-3, proxied through /api/stt/stream.
// Matches the public surface of the legacy webkitSpeechRecognition `STT` class
// so Lesson.tsx doesn't care which provider is active.

export interface DeepgramSttOptions {
  lang: string;
  onResult: (text: string, isFinal: boolean) => void;
  onError?: (err: string) => void;
  onEnd?: () => void;
  // If true, we also accumulate raw audio chunks locally so callers can grab
  // the Blob via `getAudioBlob()` after a final transcript (for pronunciation
  // scoring, etc).
  captureAudio?: boolean;
}

export class DeepgramSTT {
  private ws: WebSocket | null = null;
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private listening = false;
  // Buffer of finalized segments we accumulate until `speech_final` fires.
  private finalBuf: string[] = [];
  // Optional: raw audio chunks for post-STT pronunciation scoring.
  private audioChunks: Blob[] = [];

  constructor(private opts: DeepgramSttOptions) {}

  static isSupported(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof MediaRecorder !== "undefined" &&
      typeof WebSocket !== "undefined" &&
      MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
    );
  }

  start(): void {
    if (this.listening) return;
    this.listening = true;
    this.finalBuf = [];
    this.audioChunks = [];
    void this.run();
  }

  // Returns the accumulated audio (only if captureAudio:true was passed).
  // Safe to call after the final transcript fires.
  getAudioBlob(): Blob | null {
    if (this.audioChunks.length === 0) return null;
    return new Blob(this.audioChunks, { type: "audio/webm" });
  }

  private async run(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      this.listening = false;
      this.opts.onError?.(`mic_denied: ${err instanceof Error ? err.message : String(err)}`);
      this.opts.onEnd?.();
      return;
    }

    if (!this.listening) {
      // Aborted between getUserMedia and now.
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
      return;
    }

    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/api/stt/stream?lang=${encodeURIComponent(this.opts.lang)}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onmessage = (ev) => this.handleMessage(ev);
    ws.onerror = () => {
      this.opts.onError?.("ws_error");
    };
    ws.onclose = () => {
      this.cleanupMedia();
      this.listening = false;
      this.opts.onEnd?.();
    };

    ws.onopen = () => {
      if (!this.listening) {
        try { ws.close(1000); } catch { /* ignore */ }
        return;
      }
      try {
        const rec = new MediaRecorder(this.stream!, { mimeType: "audio/webm;codecs=opus" });
        rec.ondataavailable = async (e) => {
          if (e.data.size === 0) return;
          if (this.opts.captureAudio) {
            this.audioChunks.push(e.data);
          }
          if (ws.readyState !== ws.OPEN) return;
          try {
            const buf = await e.data.arrayBuffer();
            ws.send(buf);
          } catch { /* drop chunk */ }
        };
        rec.onerror = (ev) => {
          this.opts.onError?.(`recorder: ${(ev as unknown as { error?: { message: string } }).error?.message ?? "unknown"}`);
        };
        rec.start(250);
        this.recorder = rec;
      } catch (err) {
        this.opts.onError?.(`recorder_init: ${err instanceof Error ? err.message : String(err)}`);
        try { ws.close(1011); } catch { /* ignore */ }
      }
    };
  }

  private handleMessage(ev: MessageEvent): void {
    let raw: string;
    if (typeof ev.data === "string") raw = ev.data;
    else if (ev.data instanceof ArrayBuffer) raw = new TextDecoder().decode(ev.data);
    else return;

    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return;
    }

    const type = msg.type as string | undefined;
    if (type === "Error" || msg.error) {
      const err = (msg.error as string) ?? (msg.detail as string) ?? "stt_error";
      this.opts.onError?.(String(err));
      return;
    }

    if (type === "Results") {
      const channel = msg.channel as { alternatives?: Array<{ transcript?: string }> } | undefined;
      const alt = channel?.alternatives?.[0];
      const transcript = (alt?.transcript ?? "").trim();
      const isFinal = Boolean(msg.is_final);
      const speechFinal = Boolean(msg.speech_final);

      if (isFinal && transcript) {
        this.finalBuf.push(transcript);
      }

      if (speechFinal) {
        const full = this.finalBuf.join(" ").trim();
        this.finalBuf = [];
        if (full) {
          this.opts.onResult(full, true);
          // After a final utterance, gracefully close so callers can re-arm.
          this.stop();
        }
      } else if (!isFinal && transcript) {
        this.opts.onResult(transcript, false);
      }
      return;
    }

    if (type === "UtteranceEnd") {
      const full = this.finalBuf.join(" ").trim();
      this.finalBuf = [];
      if (full) {
        this.opts.onResult(full, true);
        this.stop();
      }
    }
  }

  stop(): void {
    if (!this.listening) return;
    this.listening = false;
    try { this.recorder?.stop(); } catch { /* ignore */ }
    if (this.ws && this.ws.readyState === this.ws.OPEN) {
      try { this.ws.send(JSON.stringify({ type: "CloseStream" })); } catch { /* ignore */ }
      // Let server close gracefully; cleanup happens in onclose.
      setTimeout(() => {
        try { this.ws?.close(1000); } catch { /* ignore */ }
      }, 250);
    } else {
      this.cleanupMedia();
    }
  }

  abort(): void {
    this.listening = false;
    try { this.recorder?.stop(); } catch { /* ignore */ }
    if (this.ws && this.ws.readyState !== this.ws.CLOSED) {
      try { this.ws.close(1000); } catch { /* ignore */ }
    }
    this.cleanupMedia();
  }

  private cleanupMedia(): void {
    try {
      this.stream?.getTracks().forEach((t) => t.stop());
    } catch { /* ignore */ }
    this.stream = null;
    this.recorder = null;
  }

  get isListening(): boolean { return this.listening; }
}
